import {
  CoObjectAdvanced,
  CoObjectItem,
  TgkAmountObjectSource,
  TgkDimensionAttributeSource,
  TgkTableObjectSource,
  TgkTextSource,
} from "../../components/interfaces/CoObject";
import { ObjectPanelValidator } from "../../components/coobjects/co-panel/ObjectPanelComponent";
import {
  fetchAmountAihComponent,
  fetchAmountComponent,
  fetchChartComponent,
  fetchDimensionAttributeComponent,
  fetchLegacyAmountAihComponent,
  fetchLegacyAmountComponent,
  fetchLegacyChartComponent,
  fetchLegacyDimensionAttributeComponent,
  fetchLegacyTableComponent,
  fetchLegacyTgkTextComponent,
  fetchTableComponent,
  fetchTgkTextComponent,
} from "../../api/object/ObjectComponentApi";
import { CoObject, SourceEnum, TgkAmount, TgkTable } from "../../api/models";
import { FormatMetadata } from "../../api/models/format-metadata";
import { TgkDimensionAttribute } from "../../api/models/tgk-dimension-attribute";
import { TgkText } from "../../api/models/tgk-text";
import { DocumentPartMetadata } from "../../api/models/document-part-metadata";
import { applyInheritedInfo } from "./InheritanceHelper";

export const isComponentInstance = (coObject: CoObjectItem) => {
  return coObject.info?.componentReference !== undefined;
};

export const isLegacyComponentInstance = (coObject: CoObjectItem): boolean => {
  return coObject.info?.componentReference?.logicId != null && coObject.info.componentReference.logicId !== "";
};

export const applyComponentDataToLegacyComponentInstance = async (
  componentInstance: CoObjectItem,
  documentPartMetadata: DocumentPartMetadata
): Promise<CoObjectItem> => {
  const componentLogicId = componentInstance.info.componentReference.logicId;
  let updatedComponentInstance: CoObjectItem;
  switch (componentInstance.info.objectType) {
    case "AMOUNT": {
      const component = await fetchLegacyAmountComponent(componentLogicId);
      if (component == null) {
        // for backward compatibility, legacy instances are always amount, we didn't know the type until we get the component
        componentInstance.info.objectType = "AMOUNT_AIH";
        return applyComponentDataToLegacyComponentInstance(componentInstance, documentPartMetadata);
      }

      completeComponentReference(componentInstance, component);
      updatedComponentInstance = await updateAmountInstanceWithComponentData(componentInstance, component);
      await applyInheritedInfo(updatedComponentInstance, documentPartMetadata);
      break;
    }
    case "AMOUNT_AIH": {
      const component = await fetchLegacyAmountAihComponent(componentLogicId);
      completeComponentReference(componentInstance, component);
      updatedComponentInstance = await updateAmountAihInstanceWithComponentData(componentInstance, component);
      await applyInheritedInfo(updatedComponentInstance, documentPartMetadata);
      break;
    }
    case "DIMENSION_ATTRIBUTE": {
      const component = await fetchLegacyDimensionAttributeComponent(componentLogicId);
      completeComponentReference(componentInstance, component);
      updatedComponentInstance = await updateDimensionAttributeInstanceWithComponentData(componentInstance, component);
      await applyInheritedInfo(updatedComponentInstance, documentPartMetadata);
      break;
    }
    case "TABLE": {
      const component = await fetchLegacyTableComponent(componentLogicId);
      completeComponentReference(componentInstance, component);
      updatedComponentInstance = await updateTableOrChartInstanceWithComponentData(componentInstance, component);
      await applyInheritedInfo(updatedComponentInstance, documentPartMetadata);
      break;
    }
    case "CHART": {
      const component = await fetchLegacyChartComponent(componentLogicId);
      completeComponentReference(componentInstance, component);
      updatedComponentInstance = await updateTableOrChartInstanceWithComponentData(componentInstance, component);
      await applyInheritedInfo(updatedComponentInstance, documentPartMetadata);
      break;
    }
    case "TGK_TEXT": {
      const component = await fetchLegacyTgkTextComponent(componentLogicId);
      completeComponentReference(componentInstance, component);
      updatedComponentInstance = await updateTgkTextInstanceWithComponentData(componentInstance, component);
      await applyInheritedInfo(updatedComponentInstance, documentPartMetadata);
      break;
    }
    default:
      console.log("Unexpected component type", componentInstance.info.objectType);
  }

  return updatedComponentInstance;
};

const completeComponentReference = (componentInstance: CoObjectItem, component: CoObject) => {
  componentInstance.info.componentReference.documentId = component.documentId;
  componentInstance.info.componentReference.contentControlId = component.contentControlId;
  componentInstance.info.componentReference.logicId = undefined;
};

export const applyComponentData = async (
  componentInstance: CoObjectItem,
  documentPartMetadata: DocumentPartMetadata
): Promise<CoObjectItem> => {
  const componentDocId = componentInstance.info.componentReference.documentId;
  const componentCcId = componentInstance.info.componentReference.contentControlId;
  let updatedComponentInstance: CoObjectItem;
  switch (componentInstance.info.objectType) {
    case "AMOUNT": {
      const component = await fetchAmountComponent(componentDocId, componentCcId.toString());
      updatedComponentInstance = await updateAmountInstanceWithComponentData(componentInstance, component);
      await applyInheritedInfo(updatedComponentInstance, documentPartMetadata);
      break;
    }
    case "AMOUNT_AIH": {
      const component = await fetchAmountAihComponent(componentDocId, componentCcId.toString());
      updatedComponentInstance = await updateAmountAihInstanceWithComponentData(componentInstance, component);
      await applyInheritedInfo(updatedComponentInstance, documentPartMetadata);
      break;
    }
    case "DIMENSION_ATTRIBUTE": {
      const component = await fetchDimensionAttributeComponent(componentDocId, componentCcId.toString());
      updatedComponentInstance = await updateDimensionAttributeInstanceWithComponentData(componentInstance, component);
      await applyInheritedInfo(updatedComponentInstance, documentPartMetadata);
      break;
    }
    case "TABLE": {
      const component = await fetchTableComponent(componentDocId, componentCcId.toString());
      updatedComponentInstance = await updateTableOrChartInstanceWithComponentData(componentInstance, component);
      await applyInheritedInfo(updatedComponentInstance, documentPartMetadata);
      break;
    }
    case "CHART": {
      const component = await fetchChartComponent(componentDocId, componentCcId.toString());
      updatedComponentInstance = await updateTableOrChartInstanceWithComponentData(componentInstance, component);
      await applyInheritedInfo(updatedComponentInstance, documentPartMetadata);
      break;
    }
    case "TGK_TEXT": {
      const component = await fetchTgkTextComponent(componentDocId, componentCcId.toString());
      updatedComponentInstance = await updateTgkTextInstanceWithComponentData(componentInstance, component);
      await applyInheritedInfo(updatedComponentInstance, documentPartMetadata);
      break;
    }
    default:
      console.log("Unexpected component type", componentInstance.info.objectType);
  }

  return updatedComponentInstance;
};

export const validateComponentData = (
  coObject: CoObjectItem,
  modifiedFields: boolean,
  modifiedSource: boolean
): ObjectPanelValidator => {
  let isAllValid = true;
  if (!coObject.info) isAllValid = false;
  else {
    if (coObject.info.objectName == null || coObject.info.objectName == "") isAllValid = false;
    // @ts-ignore
    if (coObject.info.objectType == null || coObject.info.objectType == "") isAllValid = false;
  }

  return {
    isAllValid: isAllValid,
    modifiedFields: modifiedFields,
    modifiedSource: modifiedSource,
  };
};

const updateAmountInstanceWithComponentData = async (
  componentInstance: CoObjectItem,
  component: TgkAmount
): Promise<CoObjectItem> => {
  const formatType =
    component?.source === SourceEnum.EXCEL || component?.source === SourceEnum.TAGETIK_FORM
      ? component.inheritFormat
        ? "DOCUMENT_ROOT"
        : "SOURCE"
      : (component.formatType ?? "NUMBER");

  return {
    ...componentInstance,
    info: {
      ...componentInstance.info,
      isComponent: false,
    },
    source: updateAmountSource(componentInstance, component),
    ...(component?.source === SourceEnum.TAGETIK_FORM && { parameters: component.parameters }),
    advanced: updateAdvancedInfo(componentInstance, component.formatMetadata, formatType, component.inheritFormat),
  };
};

const updateAmountAihInstanceWithComponentData = async (
  componentInstance: CoObjectItem,
  component: TgkAmount
): Promise<CoObjectItem> => {
  const formatType = component.formatType ?? "NUMBER";

  return {
    ...componentInstance,
    info: {
      ...componentInstance.info,
      objectType: "AMOUNT",
      isComponent: false,
    },
    source: updateAmountAihSource(componentInstance, component),
    advanced: updateAdvancedInfo(componentInstance, component.formatMetadata, formatType, component.inheritFormat),
  };
};

const updateDimensionAttributeInstanceWithComponentData = async (
  componentInstance: CoObjectItem,
  component: TgkDimensionAttribute
): Promise<CoObjectItem> => {
  return {
    ...componentInstance,
    info: {
      ...componentInstance.info,
      isComponent: false,
    },
    source: updateDimensionAttributeSource(componentInstance, component),
  };
};
const updateTableOrChartInstanceWithComponentData = async (
  componentInstance: CoObjectItem,
  component: TgkTable
): Promise<CoObjectItem> => {
  const formatType = component.inheritFormat ? "DOCUMENT_ROOT" : "SOURCE";
  return {
    ...componentInstance,
    originalPayload: component.originalPayload,
    info: {
      ...componentInstance.info,
      overrideData: component.overrideData,
      overrideStyle: component.overrideStyle,
      isComponent: false,
    },
    source: updateTableOrChartSource(componentInstance, component),
    ...(component?.source !== SourceEnum.EXCEL && { parameters: component.parameters }),
    advanced: updateAdvancedInfo(componentInstance, component.formatMetadata, formatType, component.inheritFormat),
  };
};

const updateTgkTextInstanceWithComponentData = async (
  componentInstance: CoObjectItem,
  component: TgkText
): Promise<CoObjectItem> => {
  return {
    ...componentInstance,
    info: {
      ...componentInstance.info,
      isComponent: false,
    },
    source: updateTgkTextSource(componentInstance, component),
  };
};

const updateAmountSource = (componentInstance: CoObjectItem, component: TgkAmount): TgkAmountObjectSource => {
  let updatedSource: TgkAmountObjectSource;
  switch (component?.source) {
    case SourceEnum.EXCEL:
      updatedSource = {
        ...componentInstance.source,
        type: "AMOUNT",
        objectSource: component?.source?.toString(),
        form: {
          code: component.formId,
          oid: mapPathToOid(component.path),
          desc: component.formDesc,
          oidPath: mapPathToOidPath(component.path),
        },
        sheet: {
          code: component.sheetCode ?? component.sheet,
          desc: component.sheet,
        },
        ...(component.area && {
          area: {
            name: component.area,
          },
        }),
        ...(component.cell && {
          cell: component.cell,
        }),
      };
      break;
    case SourceEnum.TAGETIK_FORM:
      updatedSource = {
        ...componentInstance.source,
        type: "AMOUNT",
        objectSource: component?.source?.toString(),
        dbApplication: { id: component?.dbId },
        form: {
          code: component.formId,
          oid: mapPathToOid(component.path),
          desc: component.formDesc,
          oidPath: mapPathToOidPath(component.path),
        },
        sheet: {
          code: component.sheetCode ?? component.sheet,
          desc: component.sheet,
        },
        ...(component.area && {
          area: {
            name: component.area,
          },
        }),
        ...(component.cell && {
          cell: component.cell,
        }),
      };
      break;
    default:
      updatedSource = {
        ...componentInstance.source,
        type: "AMOUNT",
        objectSource: "FORM",
        dbApplication: { id: component?.dbId },
        dimensions: component?.dimensions,
      };
      break;
  }

  return updatedSource;
};

const updateAmountAihSource = (componentInstance: CoObjectItem, component: TgkAmount): TgkAmountObjectSource => {
  return {
    ...componentInstance.source,
    type: "AMOUNT_AIH",
    objectSource: "FORM",
    dbApplication: { id: component?.dbId },
    dimensions: component?.dimensions,
    analyticalWorkspace: component?.analyticalWorkspace,
    dataset: component?.dataset,
    amount: component?.amount,
  };
};

const updateDimensionAttributeSource = (
  componentInstance: CoObjectItem,
  component: TgkDimensionAttribute
): TgkDimensionAttributeSource => {
  return {
    ...componentInstance.source,
    type: "DIMENSION_ATTRIBUTE",
    dbApplication: { id: component?.dbId },
    dimensions: component?.dimensions,
  };
};

const updateTableOrChartSource = (componentInstance: CoObjectItem, component: TgkTable): TgkTableObjectSource => {
  return {
    ...componentInstance.source,
    type: component.type === "CHART" ? "CHART" : "TABLE",
    objectSource: component?.source?.toString(),
    ...(component?.source !== SourceEnum.EXCEL && {
      dbApplication: { id: component.form?.dbId },
    }),
    form: {
      code: component.form.formId,
      oid: mapPathToOid(component.form.path),
      desc: component.form.formDesc,
      oidPath: mapPathToOidPath(component.form.path),
    },
    sheet: {
      code: component.sheetCode,
      desc: component.sheet,
    },
    ...(component.area && {
      area: {
        name: component.area,
      },
    }),
    elabResultType: component.elabResultType,
    inheritFormatImage: component.inheritFormatImage,
    formatImage: component.formatImage,
    ...(component.chart && {
      chart: {
        name: component.chart,
      },
    }),
    path: mapPathToOidPath(component.form.path),
  };
};

const updateTgkTextSource = (componentInstance: CoObjectItem, component: TgkText): TgkTextSource => {
  return {
    ...componentInstance.source,
    type: "TGK_TEXT",
    dbApplication: { id: component?.dbId },
    analyticalWorkspace: {
      code: component?.analyticalWorkspace?.code,
      description: component?.analyticalWorkspace?.description,
    },
    dataset: {
      code: component?.dataset?.code,
      description: component?.dataset?.description,
      partitionType: component?.dataset?.partitionType,
    },
    richText: {
      description: component?.richText?.description,
      id: component?.richText?.id,
      ...(component?.richText?.fieldType ? { fieldType: component.richText.fieldType } : {}),
      ...(component?.richText?.textFormatType ? { textFormatType: component.richText.textFormatType } : {}),
    },
    process: { code: component?.process?.code, numberOfCycles: component?.process?.numberOfCycles },
    dimensions: component?.dimensions,
  };
};

const updateAdvancedInfo = (
  componentInstance: CoObjectItem,
  formatMetadata: FormatMetadata,
  formatType: string,
  inheritFormat: boolean
): CoObjectAdvanced => {
  return {
    ...componentInstance.advanced,
    ...(formatMetadata && {
      formatMetadata: {
        ...formatMetadata,
        numberType: formatMetadata.numberType?.trim() || "Default",
        percentageType: formatMetadata.percentageType?.trim() || "Default",
      },
    }),
    inheritFormat: inheritFormat,
    choice: formatType,
  };
};

const mapPathToOid = (input: string | undefined | null): string => {
  if (!input) {
    return null;
  }
  const parts = input.split(",");
  return parts?.[parts?.length - 1];
};

const mapPathToOidPath = (input: string | undefined | null): string[] => {
  if (!input) {
    return [];
  }
  return input.split(",");
};
